import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';
import ControlSinFC from '@/components/ControlSinFC';
import CorregirDatosDialog from '@/components/CorregirDatosDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Heart, 
  Activity, 
  AlertTriangle,
  User,
  Calendar,
  Loader2,
  CheckCircle,
  XCircle,
  Trash2,
  FileText,
  Download,
  Info,
  Stethoscope,
  ClipboardList,
  ChevronRight,
  TrendingUp,
  Pencil,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { DownloadBlockOverlay } from '@/components/TrialBanner';
import { getDecisionFromEvaluation, intensityUnlock } from '@/utils/decisionState';
import { buildClinicalInfo } from '@/utils/clinicalInfo';
import StrengthBlock from '@/components/StrengthBlock';
import ElegirAlumnoDialog from '@/components/ElegirAlumnoDialog';
import { getActivityLabel } from '@/utils/labels';
import {
  ComparativaEntrevistas,
  AdherenciaRetest,
  PruebasFuncionalesDetalle,
  formatFecha,
} from '@/components/SeguimientoAlumno';

/**
 * Seguimiento del alumno dentro de la entrevista: lo que declaró en el retest,
 * qué cambió respecto de la entrevista anterior y sus pruebas funcionales.
 * No se muestra si la entrevista no tiene alumno o no hay nada que agregar.
 */
function SeguimientoCard({ evaluation, seguimiento, onVerFicha }) {
  if (!evaluation?.alumno_id || !seguimiento) return null;
  const { entrevistas, pruebas, comparativa } = seguimiento;

  // La entrevista anterior es la inmediatamente previa a ESTA, no la última
  // del alumno: así cada retest se compara con el que lo precedió.
  const propia = new Date(evaluation.created_at).getTime();
  const previas = entrevistas.filter(
    (e) => e.id !== evaluation.id && new Date(e.created_at).getTime() < propia
  );
  const posteriores = entrevistas.filter(
    (e) => e.id !== evaluation.id && new Date(e.created_at).getTime() > propia
  );
  const anterior = previas.length
    ? previas.reduce((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? a : b))
    : null;
  const adherencia = evaluation.retest_adherencia;

  if (!anterior && !adherencia && !pruebas.length && !posteriores.length) return null;

  return (
    <Card className="border-gray-200 bg-white" data-testid="seguimiento-card">
      <CardHeader className="border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-[#1F3A5F]" />
            <CardTitle className="font-['Inter'] text-gray-800">Seguimiento del alumno</CardTitle>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onVerFicha}
            className="gap-2 font-['Inter']"
            data-testid="seguimiento-ver-ficha"
          >
            Ver ficha del alumno
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-6">
        {posteriores.length > 0 && (
          <p className="text-sm text-gray-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            {posteriores.length === 1
              ? 'Hay 1 entrevista posterior a esta.'
              : `Hay ${posteriores.length} entrevistas posteriores a esta.`}{' '}
            La evolución completa está en la ficha del alumno.
          </p>
        )}

        {(adherencia || anterior) && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">
              {anterior
                ? `Retest: comparado con la entrevista del ${formatFecha(anterior.created_at)}`
                : 'Retest'}
            </h3>
            <AdherenciaRetest adherencia={adherencia} />
            {anterior && <ComparativaEntrevistas anterior={anterior} actual={evaluation} />}
          </div>
        )}

        {pruebas.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Pruebas funcionales</h3>
            <PruebasFuncionalesDetalle pruebas={pruebas} comparativa={comparativa} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const getIMCCategory = (imc) => {
  if (imc < 18.5) return { label: 'Bajo peso', color: 'text-blue-600', bg: 'bg-blue-50' };
  if (imc < 25) return { label: 'Normopeso', color: 'text-green-600', bg: 'bg-green-50' };
  if (imc < 30) return { label: 'Sobrepeso', color: 'text-amber-600', bg: 'bg-amber-50' };
  if (imc < 35) return { label: 'Obesidad I', color: 'text-orange-600', bg: 'bg-orange-50' };
  if (imc < 40) return { label: 'Obesidad II', color: 'text-red-600', bg: 'bg-red-50' };
  return { label: 'Obesidad III', color: 'text-red-700', bg: 'bg-red-100' };
};

export default function EvaluationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [evaluation, setEvaluation] = useState(null);
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDownloadBlock, setShowDownloadBlock] = useState(false);
  const [aptoToggling, setAptoToggling] = useState(false);

  // Seguimiento del alumno (entrevistas y pruebas funcionales). Solo si la
  // entrevista está vinculada a un alumno.
  const [seguimiento, setSeguimiento] = useState(null);

  useEffect(() => {
    loadEvaluation();
  }, [id]);

  const alumnoId = evaluation?.alumno_id;
  useEffect(() => {
    if (!alumnoId) {
      setSeguimiento(null);
      return undefined;
    }
    let cancelled = false;
    // Si falla una de las dos, se muestra lo que haya: el seguimiento es un
    // agregado de la pantalla, no puede tirar abajo la entrevista.
    Promise.allSettled([api.getAlumnoHistorial(alumnoId), api.getMediciones(alumnoId)])
      .then(([hist, pruebas]) => {
        if (cancelled) return;
        setSeguimiento({
          entrevistas: hist.status === 'fulfilled' ? hist.value?.mediciones || [] : [],
          pruebas: pruebas.status === 'fulfilled' ? pruebas.value?.mediciones || [] : [],
          comparativa: pruebas.status === 'fulfilled' ? pruebas.value?.comparativa || null : null,
        });
      });
    return () => { cancelled = true; };
  }, [alumnoId]);

  const loadEvaluation = async () => {
    try {
      const data = await api.getEvaluation(id);
      setEvaluation(data);
    } catch (error) {
      toast.error('Error al cargar entrevista');
      navigate('/alumnos');
    } finally {
      setLoading(false);
    }
  };

  // Toggle "Apto médico entregado" — marca operativa, no afecta el motor.
  const handleToggleAptoMedico = async (nextValue) => {
    if (aptoToggling || !evaluation) return;
    setAptoToggling(true);
    try {
      // Si se destilda, también limpiamos intensidades. Si se tilda, conservamos las actuales.
      const intensidades = nextValue
        ? (Array.isArray(evaluation.apto_intensidades_habilitadas)
            ? evaluation.apto_intensidades_habilitadas
            : [])
        : [];
      const updated = await api.toggleAptoMedico(id, nextValue, intensidades);
      setEvaluation((prev) => Object.assign({}, prev, {
        apto_medico_entregado: updated.apto_medico_entregado,
        apto_medico_fecha: updated.apto_medico_fecha,
        apto_intensidades_habilitadas: updated.apto_intensidades_habilitadas || [],
      }));
      toast.success(nextValue ? 'Apto médico registrado' : 'Marca de apto médico quitada');
    } catch (e) {
      toast.error('No se pudo actualizar. Intentalo de nuevo.');
    } finally {
      setAptoToggling(false);
    }
  };

  // Toggle de intensidades habilitadas — selección múltiple. Solo registro, no afecta motor.
  const handleToggleIntensidad = async (intensidad, checked) => {
    if (aptoToggling || !evaluation || !evaluation.apto_medico_entregado) return;
    const VALID = ['leve', 'moderada', 'vigorosa'];
    if (!VALID.includes(intensidad)) return;
    const current = Array.isArray(evaluation.apto_intensidades_habilitadas)
      ? evaluation.apto_intensidades_habilitadas
      : [];
    let next;
    if (checked) {
      next = current.includes(intensidad) ? current : [...current, intensidad];
    } else {
      next = current.filter((x) => x !== intensidad);
    }
    // Ordenamos según el orden canónico para consistencia visual y persistida
    next = VALID.filter((v) => next.includes(v));
    setAptoToggling(true);
    try {
      const updated = await api.toggleAptoMedico(id, true, next);
      setEvaluation((prev) => Object.assign({}, prev, {
        apto_medico_entregado: updated.apto_medico_entregado,
        apto_medico_fecha: updated.apto_medico_fecha,
        apto_intensidades_habilitadas: updated.apto_intensidades_habilitadas || [],
      }));
    } catch (e) {
      toast.error('No se pudo actualizar intensidades. Intentalo de nuevo.');
    } finally {
      setAptoToggling(false);
    }
  };

  // Panel unificado: se vuelve a la ficha del alumno, a sus entrevistas.
  const volverA = evaluation?.alumno_id
    ? `/alumnos/${evaluation.alumno_id}?tab=entrevistas`
    : '/alumnos';

  const handleDelete = async () => {
    try {
      await api.deleteEvaluation(id);
      toast.success('Entrevista eliminada');
      navigate(volverA);
    } catch (error) {
      toast.error('Error al eliminar entrevista');
    }
  };

  // La que completó el alumno por el link llega sin ficha: al revisarla va a
  // la del alumno. Si ya hay alguien con ese nombre, se pregunta antes.
  const [eleccionAlumno, setEleccionAlumno] = useState(null);
  const revisar = async (decision = null) => {
    try {
      if (!evaluation.alumno_id && !decision) {
        let candidatos = [];
        try {
          candidatos = await api.coincidenciasAlumno(evaluation.student_name);
        } catch (_e) {
          // Sin respuesta: se marca igual y el servidor no une a nadie por el nombre.
        }
        if (candidatos.length) {
          setEleccionAlumno({ nombre: evaluation.student_name, candidatos });
          return;
        }
      }
      setEleccionAlumno(null);
      await api.markEvaluationReviewed(evaluation.id, decision);
      toast.success('Entrevista marcada como revisada');
      const fresh = await api.getEvaluation(evaluation.id);
      setEvaluation(fresh);
    } catch (e) {
      toast.error(e.message || 'No se pudo marcar como revisada');
    }
  };

  const handleDownloadPDF = async () => {
    if (!evaluation) {
      toast.error('No hay datos de entrevista disponibles');
      return;
    }
    
    const toastId = toast.loading('Generando informe...');
    
    try {
      const { blob, filename } = await api.generatePdf('combined', { evaluationId: id });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      
      await refreshUser();
      toast.dismiss(toastId);
      toast.success('Informe descargado');
    } catch (err) {
      toast.dismiss(toastId);
      if (err.message.includes('upgrade') || err.message.includes('límite')) {
        setShowDownloadBlock(true);
      } else {
        toast.error('Error al generar el informe. Intente nuevamente.');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1F3A5F]" />
        </div>
      </Layout>
    );
  }

  if (!evaluation) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="font-['Inter']">Entrevista no encontrada</p>
          <Button onClick={() => navigate('/alumnos')} className="mt-4 font-['Inter']">
            Volver a Alumnos
          </Button>
        </div>
      </Layout>
    );
  }

  const imcCategory = getIMCCategory(evaluation.imc);
  const decision = getDecisionFromEvaluation(evaluation);
  const clinicalInfo = buildClinicalInfo(evaluation);

  return (
    <Layout>
      {showDownloadBlock && <DownloadBlockOverlay onClose={() => setShowDownloadBlock(false)} />}
      <CorregirDatosDialog
        evaluation={evaluation}
        open={corrigiendo}
        onOpenChange={setCorrigiendo}
        onCorregida={(actualizada) => setEvaluation({ ...evaluation, ...actualizada })}
      />
      <div className="space-y-6" data-testid="evaluation-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(volverA)}
              className="text-gray-500 hover:text-gray-700 mt-1"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 font-['Inter']">{evaluation.student_name}</h1>
              <p className="text-gray-500 text-sm flex items-center gap-2 mt-1 font-['Inter']">
                <Calendar className="h-4 w-4" />
                Entrevista del {new Date(evaluation.created_at).toLocaleDateString('es-AR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              {evaluation.correcciones?.length > 0 && (
                <p className="text-xs text-gray-400 mt-1 font-['Inter']" data-testid="nota-corregida">
                  Datos corregidos el{' '}
                  {new Date(evaluation.correcciones[evaluation.correcciones.length - 1].fecha)
                    .toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 ml-14 lg:ml-0">
            {evaluation.origen === 'alumno' && evaluation.lifecycle_status === 'pendiente_revision' && (
              <Button
                onClick={() => revisar()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-['Inter']"
                data-testid="mark-reviewed-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Marcar como revisada
              </Button>
            )}
            <Button 
              variant="outline"
              className="border-[#1F3A5F]/30 text-[#1F3A5F] hover:bg-[#1F3A5F]/5 font-['Inter'] disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleDownloadPDF()}
              disabled={evaluation.origen === 'alumno' && evaluation.lifecycle_status === 'pendiente_revision'}
              title={evaluation.origen === 'alumno' && evaluation.lifecycle_status === 'pendiente_revision'
                ? 'Marcá la entrevista como revisada para habilitar el PDF'
                : ''}
              data-testid="download-pdf"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar informe
            </Button>
            <Button
              variant="outline"
              className="border-[#1F3A5F]/30 text-[#1F3A5F] hover:bg-[#1F3A5F]/5 font-['Inter']"
              onClick={() => setCorrigiendo(true)}
              data-testid="corregir-datos-btn"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Corregir datos
            </Button>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-evaluation-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Fase B — Banner "Completado por alumno — pendiente de revisión" */}
        {evaluation.origen === 'alumno' && evaluation.lifecycle_status === 'pendiente_revision' && (
          <div
            className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-2.5 text-sm font-medium"
            data-testid="intake-pending-banner"
          >
            <ClipboardList className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span>
              Completado por alumno — pendiente de revisión. Completá los datos faltantes y marcá como revisada para habilitar el PDF.
            </span>
          </div>
        )}

        {/* Cardiovascular Alert Banner intencionalmente removido —
            la decisión y los motivos ya se muestran en el banner principal. */}

        {/* Badge verde "Apto médico entregado" — encima del banner del escenario.
            No reemplaza el color del banner, solo lo complementa. */}
        {evaluation.apto_medico_entregado && (
          <div
            className="flex items-center gap-2 bg-green-50 border border-green-300 text-green-800 rounded-xl px-4 py-2.5 text-sm font-medium"
            data-testid="apto-medico-badge"
          >
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span>
              Apto médico entregado
              {evaluation.apto_medico_fecha && (
                <span className="text-green-700/80 font-normal ml-1">
                  — registrado el {new Date(evaluation.apto_medico_fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
              )}
              {Array.isArray(evaluation.apto_intensidades_habilitadas) && evaluation.apto_intensidades_habilitadas.length > 0 && (
                <span className="text-green-700/80 font-normal ml-1" data-testid="apto-medico-intensidades-text">
                  — habilita: {evaluation.apto_intensidades_habilitadas.join(', ')}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Decision Banner — driven by motor 4-state engine */}
        <p
          className="text-xs sm:text-sm text-slate-600 mb-2 font-['Inter']"
          data-testid="decision-preheader"
        >
          Según los datos recabados en la entrevista:
        </p>
        <Card className={cn("border-2", decision.bannerClasses)} data-testid="decision-banner">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              <div className={cn("w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0", decision.iconBgClasses)}>
                <Heart className={cn("h-7 w-7", decision.iconColorClasses)} />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium uppercase tracking-wider opacity-70 mb-1 font-['Inter']">
                  Recomendación operativa
                </p>
                <h2 className="text-xl lg:text-2xl font-bold tracking-tight font-['Inter'] leading-tight" data-testid="decision-headline">
                  {decision.headline}
                </h2>
                {decision.motivos && decision.motivos.length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm opacity-90 font-['Inter']" data-testid="motivos-decision">
                    {decision.motivos.map((m, i) => (
                      <li key={i} className="flex gap-2">
                        <span aria-hidden="true">•</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Apto médico — caja propia, separada del banner del escenario.
            Es registro administrativo, no clínico: no interviene en el motor
            ni cambia el color del banner. Estaba metido adentro del banner y
            hacía que la recomendación (que es lo que hay que leer primero)
            quedara sepultada bajo un checkbox, tres chips y un párrafo legal. */}
        {evaluation && (
          <Card className="border-gray-200 bg-white" data-testid="apto-medico-card">
            <CardContent className="py-5">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                <label
                  className="inline-flex items-center gap-2 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-white transition-colors text-sm select-none"
                  data-testid="apto-medico-toggle-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={!!evaluation.apto_medico_entregado}
                    onChange={(e) => handleToggleAptoMedico(e.target.checked)}
                    disabled={aptoToggling}
                    data-testid="apto-medico-checkbox"
                    className="h-4 w-4 accent-green-600 cursor-pointer disabled:opacity-50"
                  />
                  <Stethoscope className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-gray-800">El alumno entregó el apto médico</span>
                </label>

                {/* Intensidades habilitadas — solo visibles si el apto está tildado.
                    Selección múltiple. Solo registro: NO afecta el motor. */}
                {evaluation.apto_medico_entregado && (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid="apto-medico-intensidades-wrapper"
                  >
                    <span className="text-xs font-medium text-gray-500 font-['Inter']">
                      Habilita:
                    </span>
                    {[
                      { key: 'leve', label: 'Leve' },
                      { key: 'moderada', label: 'Moderada' },
                      { key: 'vigorosa', label: 'Vigorosa' },
                    ].map((opt) => {
                      const arr = Array.isArray(evaluation.apto_intensidades_habilitadas)
                        ? evaluation.apto_intensidades_habilitadas
                        : [];
                      const checked = arr.includes(opt.key);
                      return (
                        <label
                          key={opt.key}
                          className={cn(
                            "inline-flex items-center gap-2 border rounded-lg px-3 py-1.5 cursor-pointer transition-colors text-sm select-none",
                            checked
                              ? "bg-green-50 border-green-400 text-green-800"
                              : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
                          )}
                          data-testid={`apto-intensidad-${opt.key}-wrapper`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => handleToggleIntensidad(opt.key, e.target.checked)}
                            disabled={aptoToggling}
                            data-testid={`apto-intensidad-${opt.key}-checkbox`}
                            className="h-4 w-4 accent-green-600 cursor-pointer disabled:opacity-50"
                          />
                          <span className="font-medium">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* La nota legal queda plegada: se consulta una vez y después
                  estorba, pero no se puede sacar del informe. */}
              <details className="mt-3 group">
                <summary className="text-xs text-gray-500 cursor-pointer select-none hover:text-gray-700 font-['Inter'] list-none [&::-webkit-details-marker]:hidden flex items-center gap-1">
                  <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                  Por qué se registra el apto médico
                </summary>
                <p className="mt-2 text-xs text-gray-500 leading-relaxed font-['Inter']" data-testid="apto-medico-nota">Independiente del resultado de la entrevista. En varias jurisdicciones el apto médico se exige por ley a todas las personas que entrenan, con o sin indicación clínica. Queda documentado con fecha y con las intensidades que habilitó el médico.</p>
              </details>
            </CardContent>
          </Card>
        )}

        <SeguimientoCard
          evaluation={evaluation}
          seguimiento={seguimiento}
          onVerFicha={() => navigate(`/alumnos/${evaluation.alumno_id}`)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Personal Data & Metrics */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Data */}
            <Card className="border-gray-200 bg-white">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-[#1F3A5F]" />
                  <CardTitle className="font-['Inter'] text-gray-800">Datos Personales</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Edad</p>
                    <p className="text-lg font-semibold text-gray-800 font-['Inter']">{evaluation.age} años</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Sexo Biológico</p>
                    <p className="text-lg font-semibold text-gray-800 font-['Inter']">{evaluation.sex}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Peso</p>
                    <p className="text-lg font-semibold text-gray-800 font-['Inter']">{evaluation.weight} kg</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Talla</p>
                    <p className="text-lg font-semibold text-gray-800 font-['Inter']">{evaluation.height} cm</p>
                  </div>
                  {evaluation.blood_pressure && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Presión Arterial</p>
                      <p className="text-lg font-semibold text-gray-800 font-['Inter']">{evaluation.blood_pressure} mmHg</p>
                    </div>
                  )}
                  {evaluation.resting_heart_rate && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">FC Reposo</p>
                      <p className="text-lg font-semibold text-gray-800 font-['Inter']">{evaluation.resting_heart_rate} lpm</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-['Inter']">Actividad Física</p>
                    <p className="text-lg font-semibold text-gray-800 font-['Inter']">{getActivityLabel(evaluation.activity_level)}</p>
                  </div>
                </div>

                {/* Los criterios de actividad van fuera de la grilla y como
                    pastillas en línea: dentro de una celda angosta cada etiqueta
                    se partía en tres renglones y ocupaba media tarjeta. */}
                {evaluation.does_planned_exercise != null && (
                  <div className="mt-5 pt-4 border-t border-gray-100" data-testid="activity-criteria-detail">
                    {evaluation.does_planned_exercise === true ? (
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { key: 'act_moderate_intensity', label: 'Intensidad moderada o superior' },
                          { key: 'act_min_30', label: '≥30 min por sesión' },
                          { key: 'act_freq_3', label: '≥3 veces por semana' },
                          { key: 'act_sustained_3mo', label: 'Sostenido ≥3 meses' },
                        ].map((c) => {
                          const cumple = evaluation[c.key] === true;
                          return (
                            <span
                              key={c.key}
                              data-testid={`activity-criterion-${c.key}`}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap ${
                                cumple
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                  : 'border-gray-200 bg-gray-50 text-gray-400'
                              }`}
                            >
                              {cumple
                                ? <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                                : <XCircle className="h-3.5 w-3.5 shrink-0" />}
                              {c.label}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 font-['Inter']">No realiza ejercicio físico planificado</p>
                    )}
                  </div>
                )}

                {/* Información clínica relevante: vive acá, junto a los datos de
                    la persona, en vez de en una tarjeta aparte de la derecha. */}
                <div className="mt-5 pt-4 border-t border-gray-100" data-testid="clinical-info-inline">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-['Inter']">
                    Información clínica relevante
                  </p>
                  <p className="text-xs text-gray-400 font-['Inter'] mb-2.5">
                    Datos no decisorios — para consideración profesional
                  </p>
                  {clinicalInfo.length === 0 ? (
                    <p className="text-sm text-gray-400 italic font-['Inter']">
                      Sin observaciones contextuales.
                    </p>
                  ) : (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {clinicalInfo.map((it, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 font-['Inter']">
                          <span className="text-gray-300 mt-0.5" aria-hidden="true">•</span>
                          <span className="flex-1 break-words">
                            {it.label}
                            {it.value && <span className="font-semibold ml-1">{it.value}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Calculated Metrics */}
            <Card className="border-gray-200 bg-white">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-[#1F3A5F]" />
                  <div>
                    <CardTitle className="font-['Inter'] text-gray-800">Métricas Calculadas</CardTitle>
                    <CardDescription className="text-gray-500 font-['Inter']">Valores derivados de los datos antropométricos</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* IMC */}
                  <div className={cn("p-4 rounded-lg border", imcCategory.bg, "border-current/20")}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-600 uppercase tracking-wider font-['Inter']">IMC</p>
                      <Badge variant="outline" className={cn("text-xs font-['Inter']", imcCategory.color)}>{imcCategory.label}</Badge>
                    </div>
                    <p className="text-3xl font-bold font-mono text-gray-800">{evaluation.imc} <span className="text-sm font-normal">kg/m²</span></p>
                    <p className="text-[10px] text-gray-400 mt-2 font-['Inter']">
                      Normal: 18.5-24.9 | Sobrepeso: 25-29.9 | Obesidad: ≥30
                    </p>
                  </div>
                  
                  {/* FCM */}
                  <div className="p-4 rounded-lg bg-[#1F3A5F]/5 border border-[#1F3A5F]/20">
                    <p className="text-xs text-gray-600 uppercase tracking-wider mb-2 font-['Inter']">FC Máxima Teórica</p>
                    <p className="text-3xl font-bold font-mono text-gray-800">{evaluation.max_heart_rate} <span className="text-sm font-normal">lpm</span></p>
                    <p className="text-[10px] text-gray-500 mt-2 font-['Inter']">
                      Fórmula: 208 - (0.7 × edad)
                    </p>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Heart Rate Zones */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2 font-['Inter']">
                    <Heart className="h-4 w-4 text-red-500" />
                    {evaluation.betabloqueante === true ? 'Control de intensidad' : 'Zonas de Entrenamiento'}
                  </h4>
                  {(() => {
                    const unlock = intensityUnlock(
                      evaluation.exercise_recommendation?.estado,
                      evaluation.apto_medico_entregado,
                      evaluation.apto_intensidades_habilitadas,
                    );
                    // Con betabloqueante la FC está frenada: Borg y test del habla.
                    if (evaluation.betabloqueante === true) return <ControlSinFC unlock={unlock} />;
                    // La zona leve solo existe en entrevistas emitidas con el
                    // criterio ACSM. Las anteriores conservan sus 2 zonas y sus
                    // rótulos originales — no se recalculan.
                    const hasLight = evaluation.light_zone_min !== null
                      && evaluation.light_zone_min !== undefined;
                    const zones = hasLight
                      ? [
                          { key: 'light', label: 'Zona Leve (30-39%)', tone: 'blue',
                            min: evaluation.light_zone_min, max: evaluation.light_zone_max },
                          { key: 'moderate', label: 'Zona Moderada (40-59%)', tone: 'green',
                            min: evaluation.moderate_zone_min, max: evaluation.moderate_zone_max },
                          { key: 'vigorous', label: 'Zona Vigorosa (60-89%)', tone: 'orange',
                            min: evaluation.vigorous_zone_min, max: evaluation.vigorous_zone_max },
                        ]
                      : [
                          { key: 'moderate', label: 'Zona Leve a Moderada (50-70%)', tone: 'green',
                            min: evaluation.moderate_zone_min, max: evaluation.moderate_zone_max },
                          { key: 'vigorous', label: 'Zona Vigorosa (70-85%)', tone: 'orange',
                            min: evaluation.vigorous_zone_min, max: evaluation.vigorous_zone_max },
                        ];
                    // Clases completas y estáticas: Tailwind no puede purgar
                    // nombres construidos por concatenación.
                    const TONES = {
                      blue:   { box: 'p-4 border border-blue-200 rounded-lg bg-blue-50',
                                dot: 'w-3 h-3 rounded-full bg-blue-500',
                                title: "font-medium text-blue-800 text-sm font-['Inter']",
                                value: 'text-2xl font-mono font-bold text-blue-700' },
                      green:  { box: 'p-4 border border-green-200 rounded-lg bg-green-50',
                                dot: 'w-3 h-3 rounded-full bg-green-500',
                                title: "font-medium text-green-800 text-sm font-['Inter']",
                                value: 'text-2xl font-mono font-bold text-green-700' },
                      orange: { box: 'p-4 border border-orange-200 rounded-lg bg-orange-50',
                                dot: 'w-3 h-3 rounded-full bg-orange-500',
                                title: "font-medium text-orange-800 text-sm font-['Inter']",
                                value: 'text-2xl font-mono font-bold text-orange-700' },
                    };
                    return (
                      <div className={hasLight
                        ? 'grid grid-cols-1 md:grid-cols-3 gap-4'
                        : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                        {zones.map((z) => {
                          const t = TONES[z.tone];
                          return unlock[z.key].enabled ? (
                            <div key={z.key} className={t.box} data-testid={`zone-${z.key}-enabled`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={t.dot} />
                                <span className={t.title}>{z.label}</span>
                              </div>
                              <p className={t.value}>
                                {z.min} - {z.max} <span className="text-sm font-normal">lpm</span>
                              </p>
                            </div>
                          ) : (
                            <div key={z.key} className="p-4 border border-gray-200 rounded-lg bg-gray-50 opacity-70" data-testid={`zone-${z.key}-blocked`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-3 h-3 rounded-full bg-gray-400" />
                                <span className="font-medium text-gray-500 text-sm font-['Inter']">{z.label}</span>
                              </div>
                              <p className="text-xs italic text-gray-500 leading-relaxed">
                                {unlock[z.key].reason}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Studies */}
            {(evaluation.has_lab_recent || evaluation.has_ecg || evaluation.has_stress_test) && (
              <Card className="border-gray-200 bg-white">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="font-['Inter'] text-gray-800">Estudios Complementarios</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {evaluation.has_lab_recent && (
                      <Badge variant="outline" className="gap-1 border-[#1F3A5F]/30 bg-[#1F3A5F]/5 text-[#1F3A5F] font-['Inter']">
                        <CheckCircle className="h-3 w-3" />
                        Laboratorio
                      </Badge>
                    )}
                    {evaluation.has_ecg && (
                      <Badge variant="outline" className="gap-1 border-[#1F3A5F]/30 bg-[#1F3A5F]/5 text-[#1F3A5F] font-['Inter']">
                        <CheckCircle className="h-3 w-3" />
                        ECG
                      </Badge>
                    )}
                    {evaluation.has_stress_test && (
                      <Badge variant="outline" className="gap-1 border-[#1F3A5F]/30 bg-[#1F3A5F]/5 text-[#1F3A5F] font-['Inter']">
                        <CheckCircle className="h-3 w-3" />
                        Ergometría
                      </Badge>
                    )}
                  </div>
                  
                  {evaluation.has_lab_recent && (evaluation.hdl || evaluation.glucose || evaluation.cholesterol_total || evaluation.ldl || evaluation.triglycerides) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                      {evaluation.cholesterol_total && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1 font-['Inter']">Col. Total</p>
                          <p className="text-lg font-semibold font-mono text-gray-800">{evaluation.cholesterol_total} mg/dl</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-['Inter']">
                            {evaluation.cholesterol_total >= 240 ? 'Alto' : evaluation.cholesterol_total >= 200 ? 'Limítrofe' : 'Deseable'}
                          </p>
                        </div>
                      )}
                      {evaluation.hdl && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1 font-['Inter']">HDL</p>
                          <p className="text-lg font-semibold font-mono text-gray-800">{evaluation.hdl} mg/dl</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-['Inter']">
                            {evaluation.hdl < 40 ? 'Bajo (<40)' : evaluation.hdl >= 60 ? 'Protector (≥60)' : 'Normal'}
                          </p>
                        </div>
                      )}
                      {evaluation.ldl && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1 font-['Inter']">LDL</p>
                          <p className="text-lg font-semibold font-mono text-gray-800">{evaluation.ldl} mg/dl</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-['Inter']">
                            {evaluation.ldl >= 160 ? 'Alto' : evaluation.ldl >= 130 ? 'Limítrofe' : 'Óptimo'}
                          </p>
                        </div>
                      )}
                      {evaluation.triglycerides && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1 font-['Inter']">Triglicéridos</p>
                          <p className="text-lg font-semibold font-mono text-gray-800">{evaluation.triglycerides} mg/dl</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-['Inter']">
                            {evaluation.triglycerides >= 200 ? 'Alto' : evaluation.triglycerides >= 150 ? 'Limítrofe' : 'Normal'}
                          </p>
                        </div>
                      )}
                      {evaluation.glucose && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1 font-['Inter']">Glucemia</p>
                          <p className="text-lg font-semibold font-mono text-gray-800">{evaluation.glucose} mg/dl</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-['Inter']">
                            {evaluation.glucose >= 126 ? 'Elevada' : evaluation.glucose >= 100 ? 'Alterada (>100)' : 'Normal'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Información clínica relevante & operativos */}
          <div className="space-y-6">
            {/* Recomendaciones operativas */}

            <Card className="border-gray-200 bg-white" data-testid="recomendaciones-card">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-[#1F3A5F]" />
                  <CardTitle className="font-['Inter'] text-gray-800">Recomendaciones</CardTitle>
                </div>
              </CardHeader>
              {/* Misma grilla de dos columnas que las recomendaciones de fuerza,
                  para que las cajas de cardio y de fuerza queden alineadas. */}
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-2.5">
                  <div className={cn("px-3 py-2.5 rounded-xl border", decision.bannerClasses)}>
                    <p className="text-[10px] uppercase tracking-wide opacity-70 font-['Inter'] whitespace-nowrap overflow-hidden text-ellipsis">Moderado</p>
                    <p className="text-sm font-bold mt-0.5 font-['Inter'] leading-snug" data-testid="rec-moderate">{decision.moderate}</p>
                  </div>
                  <div className={cn("px-3 py-2.5 rounded-xl border", decision.bannerClasses)}>
                    <p className="text-[10px] uppercase tracking-wide opacity-70 font-['Inter'] whitespace-nowrap overflow-hidden text-ellipsis">Vigoroso</p>
                    <p className="text-sm font-bold mt-0.5 font-['Inter'] leading-snug" data-testid="rec-vigorous">{decision.vigorous}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recomendaciones de fuerza (condicional por escenario) */}
            <Card className="border-gray-200 bg-white" data-testid="strength-card">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="font-['Inter'] text-gray-800 text-base">
                  Recomendaciones de fuerza
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <StrengthBlock estado={evaluation?.exercise_recommendation?.estado} />
              </CardContent>
            </Card>

            {/* Conducta operativa (no prescriptiva) */}
            <Card className="border-gray-200 bg-white" data-testid="conducta-card">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="font-['Inter'] text-gray-800 text-base">Conducta operativa</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className={cn("p-3 rounded-lg border", decision.bannerClasses)}>
                  <p className="text-sm font-medium font-['Inter']" data-testid="conducta-text">
                    {decision.conducta}
                  </p>
                </div>
                <p
                  className="mt-2 text-[10px] italic text-gray-400 font-['Inter']"
                  data-testid="conducta-citation"
                >
                  Basado en algoritmo de screening preparticipativo ACSM (Riebe et al., 2015) y guías AHA/ESC.
                </p>
              </CardContent>
            </Card>


            {evaluation.has_limitations && evaluation.affected_joint && (
              <Card className="border-gray-200 bg-white">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="font-['Inter'] text-gray-800">Limitaciones</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm text-gray-700 leading-relaxed font-['Inter']">
                    {evaluation.affected_joint}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Legal Disclaimer */}
            <div className="p-4 bg-gray-100 rounded-lg space-y-2" data-testid="legal-disclaimer">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-gray-500 leading-relaxed font-['Inter']">
                  Esta herramienta es orientativa y no reemplaza valoración médica.
                  La interpretación y prescripción final es responsabilidad del profesional.
                  Tratamiento de datos conforme Ley 25.326.
                </p>
              </div>
              <p className="text-[10px] text-gray-500 italic leading-relaxed font-['Inter'] pl-6">
                Esta entrevista es un protocolo operativo de detección preliminar realizado por profesional
                de la actividad física. No constituye diagnóstico médico ni reemplaza la evaluación de un
                profesional de la salud. La decisión final de iniciar, continuar o suspender un programa
                de ejercicio es responsabilidad compartida entre el profesional, el alumno y el médico
                tratante cuando corresponda.
              </p>
            </div>
          </div>
        </div>

        {/* Delete Dialog */}
        <ElegirAlumnoDialog
          abierto={!!eleccionAlumno}
          nombre={eleccionAlumno?.nombre}
          candidatos={eleccionAlumno?.candidatos}
          onElegir={(id) => revisar({ alumno_id: id })}
          onNueva={() => revisar({ alumno_nuevo: true })}
          onCancelar={() => setEleccionAlumno(null)}
        />

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-['Inter']">¿Eliminar entrevista?</AlertDialogTitle>
              <AlertDialogDescription className="font-['Inter']">
                Esta acción no se puede deshacer. La entrevista de <strong>{evaluation.student_name}</strong> será eliminada permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-gray-200 font-['Inter']" data-testid="cancel-delete">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white font-['Inter']"
                data-testid="confirm-delete"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

function _UnusedFactorItem() { return null; }
